import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Adam Intop → Extended modules.
 * Perechile vechi: `settings/easeedesk/adam_intop_extended_modules`,
 * `getAdamIntopExtendedModules`, `edit_extended_module`,
 * `create_extended_module_save`, `edit_extended_module_save`,
 * `delete_extended_module`, `create_extended_module`, plus cele doua capete de
 * Dialogflow pe care harta le tine tot in grupul asta (`getCompaniesDF`,
 * `save_adam_intop_dialogflow_settings`).
 *
 * `apiGet`/`apiPost` pun singure `/api/v2` in fata caii.
 */

const BASE = '/settings/easeedesk/adam_intop/extended_modules'

export interface AdamModuleBrief {
  ID: number
  module_name: string
}

export interface AdamModulesShell {
  title: string
  moduleUrl: string
  modules: AdamModuleBrief[]
}

export interface AdamModuleRow {
  ID: number
  module_name: string
  eligible_for_python: string
  active_in_adam_intop: string
  status: string
  created_at: string
  updated_at: string
  DT_RowId: string
}

export interface AdamModuleTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: AdamModuleRow[]
}

export interface AdamModulePage {
  module: Omit<AdamModuleRow, 'DT_RowId'>
  modules: AdamModuleBrief[]
}

export interface AdamModuleForm {
  module_name: string
  eligible_for_python: string
  active_in_adam_intop: string
  status: string
}

export interface AdamModuleTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

/** Aceiasi parametri pe care ii trimitea DataTables pe `getAdamIntopExtendedModules`. */
export function adamModuleTableParams(r: AdamModuleTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function adamModulesShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'extended_modules'],
    queryFn: () => apiGet<AdamModulesShell>(BASE),
  })
}

export function adamModuleTableQuery(r: AdamModuleTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'extended_modules', 'table', r],
    queryFn: () => apiGet<AdamModuleTablePage>(`${BASE}/table?${adamModuleTableParams(r).toString()}`),
  })
}

export function adamModuleQuery(module: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'extended_modules', 'one', module],
    queryFn: () => apiGet<AdamModulePage>(`${BASE}/${module}`),
    enabled: module > 0,
  })
}

/** `create_extended_module_save`. */
export function createAdamModule(form: AdamModuleForm) {
  return apiPost<{ ok?: boolean; ID?: number }>(BASE, { ...form })
}

/** `edit_extended_module_save`. */
export function saveAdamModule(module: number, form: AdamModuleForm) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${module}`, { ...form })
}

/**
 * `delete_extended_module`, dar cu id-ul in adresa.
 *
 * In portal butonul de stergere al modulelor chema capul FISIERELOR
 * (`delete_python_file`), deci modulul ramanea pe loc si se stingea un fisier
 * cu acelasi id. Aici cele doua stergeri au adrese diferite.
 */
export function removeAdamModule(module: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${module}/remove`, {})
}

/** `create_extended_module`: o resursa noua in `it_module_resources`. */
export function createAdamModuleResource(module: number, resource_name: string, resource_slug: string) {
  return apiPost<{ ok?: boolean }>('/settings/easeedesk/adam_intop/module_resources', {
    module,
    resource_name,
    resource_slug,
  })
}

export interface DialogflowCompany {
  ID: number
  name: string
}

/** `adam_intop_dialogflow_settings`: listele, comutatoarele si ce e ales acum. */
export interface DialogflowShell {
  providerName: string
  all_ticket_types: { ID: number; type_name: string; short_name: string }[]
  chat_ticket_sources: { chat_source: string; source_name: string }[]
  selected_companies: string[]
  selected_ticket_types: string[]
  selected_chat_ticket_sources: string[]
  status: number
  connect_with_adam_analyser: number
  can_make_ticket_changes: number
  force_takeover: number
}

export function dialogflowShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'dialogflow'],
    queryFn: () => apiGet<DialogflowShell>('/settings/easeedesk/adam_intop/dialogflow'),
  })
}

/** `getCompaniesDF`: firmele furnizorului, pentru ecranul Dialogflow. */
export function dialogflowCompaniesQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'adam_intop', 'dialogflow', 'companies'],
    queryFn: () => apiGet<{ companies: DialogflowCompany[] }>('/settings/easeedesk/adam_intop/dialogflow/companies'),
  })
}

export interface DialogflowForm {
  status: boolean
  connect_with_adam_analyser: boolean
  can_make_ticket_changes: boolean
  force_takeover: boolean
  companies: number[]
  ticket_types: number[]
  chat_sources: string[]
  ticket_sources: string
}

/**
 * `save_adam_intop_dialogflow_settings`.
 *
 * Ecranul: `AdamIntopDialogflowPage`.
 */
export function saveDialogflowSettings(form: DialogflowForm) {
  return apiPost<{ ok?: boolean }>('/settings/easeedesk/adam_intop/dialogflow', {
    status: form.status ? 'on' : '',
    connect_with_adam_analyser: form.connect_with_adam_analyser ? 'on' : '',
    can_make_ticket_changes: form.can_make_ticket_changes ? 'on' : '',
    force_takeover: form.force_takeover ? 'on' : '',
    'companies[]': form.companies,
    'ticket_types[]': form.ticket_types,
    'chat_sources[]': form.chat_sources,
    ticket_sources: form.ticket_sources,
  })
}
